import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Panel, LiveTag } from "../components/common/Panel";
import { getValidators, getChainInfo, getValidatorLeaderboard } from "../lib/api";
import { fmtNum, shorten, CHAIN } from "../lib/format";
import { ResponsiveContainer, BarChart, Bar, Tooltip, Cell } from "recharts";

export default function Validators() {
  const [data, setData] = useState(null);
  const [info, setInfo] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [v, i, lb] = await Promise.all([getValidators(), getChainInfo(), getValidatorLeaderboard().catch(() => null)]);
        if (!alive) return;
        setData(v); setInfo(i); if (lb) setBoard(lb);
      } catch {}
    };
    load();
    const id = setInterval(load, 6000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const vals = data?.validators || [];
  const colors = ["var(--amber)", "var(--blue)", "var(--green)", "#B98BF0"];
  const chartData = vals.map((v, i) => ({ name: `V${i + 1}`, blocks: v.recentBlocks, i }));

  return (
    <div className="fade-in" data-testid="validators-page">
      <div className="mb-4">
        <span className="eyebrow" style={{ color: "var(--amber)" }}>QBFT Consensus</span>
        <h1 className="font-display text-[24px] font-extrabold mt-1">Validators</h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--muted)" }}>
          Byzantine Fault Tolerant validator set. Tolerates up to {vals.length ? Math.floor((vals.length - 1) / 3) : 1} faulty node(s) — needs ≥{vals.length ? Math.ceil((2 * vals.length) / 3) : 3} to finalize blocks.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Panel title="Active Validator Set" action={<LiveTag />} bodyClass="overflow-auto">
            <table className="dt">
              <thead><tr><th>#</th><th>Validator Address</th><th>Blocks (recent)</th><th>Status</th></tr></thead>
              <tbody>
                {vals.map((v, i) => (
                  <tr key={v.address} data-testid={`validator-row-${i}`}>
                    <td className="mono">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-bold" style={{ background: `${colors[i % 4]}22`, color: colors[i % 4] }}>{i + 1}</span>
                    </td>
                    <td><Link to={`/address/${v.address}`} className="link-amber mono text-[12.5px]">{v.address}</Link></td>
                    <td className="mono">{v.recentBlocks}</td>
                    <td><span className="pill pill-green"><span className="dot dot-green live-dot" /> Validating</span></td>
                  </tr>
                ))}
                {vals.length === 0 && <tr><td colSpan={4} className="text-center py-10" style={{ color: "var(--muted)" }}>Loading…</td></tr>}
              </tbody>
            </table>
          </Panel>
        </div>

        <div className="flex flex-col gap-5">
          <Panel title="Consensus">
            <div className="p-5 space-y-4">
              <div className="flex justify-between"><span className="eyebrow">Algorithm</span><span className="pill pill-amber">QBFT</span></div>
              <div className="flex justify-between"><span className="eyebrow">Validators</span><span className="mono">{vals.length}</span></div>
              <div className="flex justify-between"><span className="eyebrow">Block Period</span><span className="mono">3s</span></div>
              <div className="flex justify-between"><span className="eyebrow">Latest Block</span><span className="mono">#{info ? fmtNum(info.latestBlock) : "—"}</span></div>
              <div className="flex justify-between"><span className="eyebrow">Finality</span><span className="pill pill-green">Instant</span></div>
            </div>
          </Panel>
          <Panel title="Block Proposals" action={<span className="eyebrow">last {data?.sampleWindow || 40}</span>}>
            <div className="p-4" style={{ height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <Tooltip cursor={{ fill: "rgba(245,166,35,0.08)" }} contentStyle={{ background: "var(--panel-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
                  <Bar dataKey="blocks" radius={[3, 3, 0, 0]}>
                    {chartData.map((e, i) => <Cell key={i} fill={colors[i % 4]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>
      </div>

      {/* All-time validator fee leaderboard */}
      <Panel title="Validator Leaderboard" subtitle="all-time fees & blocks" action={<LiveTag />} className="mt-5" bodyClass="overflow-auto" testId="panel-validator-leaderboard">
        <table className="dt">
          <thead><tr><th>Rank</th><th>Validator</th><th>Blocks Proposed</th><th>Share</th><th>Fees Earned</th><th>Status</th></tr></thead>
          <tbody>
            {(board?.leaderboard || []).map((r) => {
              const share = board.totalBlocks ? (r.blocks / board.totalBlocks) * 100 : 0;
              return (
                <tr key={r.validator} data-testid={`leaderboard-row-${r.rank}`}>
                  <td><span className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-bold" style={{ background: `${colors[(r.rank - 1) % 4]}22`, color: colors[(r.rank - 1) % 4] }}>{r.rank}</span></td>
                  <td><Link to={`/address/${r.validator}`} className="link-amber mono text-[12.5px]">{r.validator}</Link></td>
                  <td className="mono">{fmtNum(r.blocks)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="rounded-full overflow-hidden" style={{ width: 80, height: 6, background: "var(--bg-2)" }}>
                        <div style={{ width: `${share}%`, height: "100%", background: "var(--amber)" }} />
                      </div>
                      <span className="mono text-[11px]" style={{ color: "var(--muted)" }}>{share.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="mono" style={{ color: "var(--amber)" }}>{r.feeBcc.toFixed(6)} {CHAIN.symbol}</td>
                  <td>{r.isActive ? <span className="pill pill-green">Active</span> : <span className="pill pill-muted">Inactive</span>}</td>
                </tr>
              );
            })}
            {!board && <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--muted)" }}>Computing…</td></tr>}
          </tbody>
        </table>
        {board && (
          <div className="px-4 py-3 flex items-center justify-between text-[12px] mono" style={{ borderTop: "1px solid var(--border)", color: "var(--muted)" }}>
            <span>{fmtNum(board.totalBlocks)} blocks · {board.currentFeeGwei} Gwei fee</span>
            <span data-testid="leaderboard-total-fees">Total fees: <span style={{ color: "var(--amber)" }}>{board.totalFeeBcc.toFixed(6)} {CHAIN.symbol}</span></span>
          </div>
        )}
      </Panel>
    </div>
  );
}
